import { collection, getDocs, query } from 'firebase/firestore';
import { db } from '../firebase/config';
import * as XLSX from 'xlsx';

export const exportDashboardToExcel = async (animals, shares, branchName) => {
  try {
    // 1. Tüm ödemeleri veritabanından çek (Client-side memory filter daha güvenlidir)
    const paymentsSnap = await getDocs(collection(db, 'payments'));
    const allPayments = paymentsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // 2. Excel Veri Dizisi Hazırla
    const excelData = [];

    // Hayvanları Kesim Sırasına göre sırala
    const sortedAnimals = [...animals].sort((a, b) => {
      const pA = parseInt(a.slaughterNo, 10);
      const pB = parseInt(b.slaughterNo, 10);
      if (isNaN(pA) && isNaN(pB)) return 0;
      if (isNaN(pA)) return 1;
      if (isNaN(pB)) return -1;
      return pA - pB;
    });

    sortedAnimals.forEach(animal => {
      // O hayvana ait hissedarları bul
      const animalShares = shares.filter(s => s.animalId === animal.id);
      
      const animalBaseInfo = {
        'Şube': branchName,
        'Kesim Sırası': animal.slaughterNo || '-',
        'Hayvan No': animal.animalNo || '-',
        'Küpe Numarası': animal.tagNo || '-',
        'Hayvan Türü': animal.type === 'buyukbas' ? 'Büyükbaş' : 'Küçükbaş',
        'Hayvan Fiyatı': `${Number(animal.price || 0).toLocaleString('tr-TR')} ₺`,
        'Satılan Hisse Miktarı': animal.shareCount || 0,
        'Boş Hisseler (Kalan)': (Number(animal.totalShares) || 7) - (Number(animal.shareCount) || 0)
      };

      if (animalShares.length === 0) {
        // Hiç hissedar yoksa bile hayvanı raporda göster (Boş olarak)
        excelData.push({
          ...animalBaseInfo,
          'Hissedar Adı': 'Boş - Satış Yok',
          'Vekalet Durumu': '-',
          'Telefon Numarası': '-',
          'Hissenin Toplam Fiyatı': '-',
          'Toplam Kalan Borç': '-',
          'Ödeme Tipi': '-',
          'Tahsilat Tarihi': '-',
          'Tahsilat Tutarı (Bu Makbuz)': '-',
          'Makbuz/Dekont Görseli': '-',
          'Eklenen Not': '-'
        });
      }

      // Her bir hissedar için
      animalShares.forEach(share => {
        // Hissedara ait tahsilat (ödeme) geçmişini bul
        const sharePayments = allPayments.filter(p => p.shareId === share.id);
        const shareholderName = `${share.customerFirstName || ''} ${share.customerLastName || ''}`.trim();
        const sharePrice = Number(share.price || 0);
        const totalPaidAmt = Number(share.paidAmount || 0);
        const debt = sharePrice - totalPaidAmt;

        const shareBaseInfo = {
          ...animalBaseInfo,
          'Hissedar Adı': shareholderName,
          'Hissenin Toplam Fiyatı': `${sharePrice.toLocaleString('tr-TR')} ₺`,
          'Toplam Kalan Borç': `${debt.toLocaleString('tr-TR')} ₺`,
          'Vekalet Durumu': share.isProxyGiven ? 'Verildi' : 'Bekliyor',
          'Telefon Numarası': share.customerPhone || '-',
        };

        if (sharePayments.length === 0) {
           // Ödemesi hiç yoksa (Sıfır ödeme ama satış yapılmış)
           excelData.push({
              ...shareBaseInfo,
              'Ödeme Tipi': 'Ödeme Alınmadı',
              'Tahsilat Tarihi': '-',
              'Tahsilat Tutarı (Bu Makbuz)': '-',
              'Makbuz/Dekont Görseli': '-',
              'Eklenen Not': '-'
           });
        } else {
           // Bu hissedarın tüm ödemelerini zaman sırasına göre diz
           sharePayments.sort((a, b) => {
             const timeA = a.createdAt?.seconds || 0;
             const timeB = b.createdAt?.seconds || 0;
             return timeA - timeB;
           });

           // Yapılan YATIRIM/ÖDEME kadar satır ekle ki hepsi tarih tarih alt alta çıksın
           sharePayments.forEach(payment => {
              const pDate = payment.createdAt 
                 ? new Date(payment.createdAt.seconds * 1000).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                 : '-';
              
              excelData.push({
                 ...shareBaseInfo,
                 'Ödeme Tipi': payment.type || 'Nakit',
                 'Tahsilat Tutarı (Bu Makbuz)': `${Number(payment.amount || 0).toLocaleString('tr-TR')} ₺`,
                 'Tahsilat Tarihi': pDate,
                 'Makbuz/Dekont Görseli': payment.receiptUrl || 'Yok',
                 'Eklenen Not': payment.description || '-'
              });
           });
        }
      });
    });

    if (excelData.length === 0) {
      throw new Error("Seçilen filtrelerde dışa aktarılacak herhangi bir veri bulunamadı.");
    }

    // 3. Excel Çalışma Kitabını Oluştur
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Sütun genişliklerini içeriğe uygun ayarla ki göze hoş görünsün
    const wscols = [
      { wch: 15 }, // Şube
      { wch: 15 }, // Kesim Sırası
      { wch: 15 }, // Hayvan No
      { wch: 18 }, // Küpe Numarası
      { wch: 15 }, // Tür
      { wch: 18 }, // Hayvan Fiyatı
      { wch: 20 }, // Satılan Hisse Miktarı
      { wch: 20 }, // Boş Hisseler
      { wch: 30 }, // İsim
      { wch: 25 }, // Hisse Toplam
      { wch: 20 }, // Toplam Kalan Borç
      { wch: 15 }, // Vekalet
      { wch: 18 }, // Tel
      { wch: 15 }, // Ödeme Tipi
      { wch: 25 }, // Tutar
      { wch: 20 }, // Tarih
      { wch: 60 }, // Link
      { wch: 35 }, // Not
    ];
    worksheet['!cols'] = wscols;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Tahsilat_Detayli_Rapor");

    // 4. Dosyayı Kullanıcıya İndirt
    const today = new Date().toLocaleDateString('tr-TR').replace(/\./g, '-');
    const safeBranchName = (branchName || 'TumSistem').replace(/[^a-zA-Z0-9çğıöşüÇĞİÖŞÜ_]/g, '_');
    const fileName = `Kurban_Raporu_${safeBranchName}_${today}.xlsx`;

    XLSX.writeFile(workbook, fileName);

    return true;
  } catch (error) {
    console.error('Excel Oluşturma Hatası:', error);
    throw new Error(error.message);
  }
};
